import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material'
import { TelegramUser, UserPermission } from 'models'

interface PermissionChipsProps {
  permissions: Array<UserPermission>
}

const PermissionChips = ({ permissions }: PermissionChipsProps) => {
  return Object.values(UserPermission).map((botName) => {
    const hasPermission = permissions.includes(botName)
    return (
      <Chip
        key={botName}
        label={botName.replace('-bot', '').replace('-', ' ')}
        color={hasPermission ? 'success' : 'default'}
        size="small"
        variant={hasPermission ? 'filled' : 'outlined'}
        sx={{ textTransform: 'capitalize' }}
      />
    )
  })
}

interface UserCardProps {
  telegramUser: TelegramUser
  handleToggleActivation: (
    telegramUserId: string,
    shouldActivate: boolean
  ) => Promise<void>
  handleEdit: (telegramUser: TelegramUser) => void
}

const UserCard = ({
  telegramUser,
  handleToggleActivation,
  handleEdit,
}: UserCardProps) => {
  const isDeactivated = telegramUser.deactivated_at !== null
  return (
    <Card sx={{ borderLeft: isDeactivated ? 3 : 0, borderColor: 'error.main' }}>
      <CardContent>
        <Stack spacing={1}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                variant="h6"
                sx={{
                  textDecoration: isDeactivated ? 'line-through' : 'none',
                  color: isDeactivated ? 'text.disabled' : 'text.primary',
                }}
              >
                {telegramUser.name}
              </Typography>
              {isDeactivated && (
                <Chip
                  label="DEACTIVATED"
                  color="error"
                  size="small"
                  variant="filled"
                  sx={{ fontWeight: 'bold' }}
                />
              )}
            </Box>

            <Button
              variant={isDeactivated ? 'contained' : 'outlined'}
              color={isDeactivated ? 'success' : 'error'}
              size="small"
              onClick={async () => {
                handleToggleActivation(telegramUser._id, isDeactivated)
              }}
            >
              {isDeactivated ? 'Activate' : 'Deactivate'}
            </Button>
          </Box>

          <Box>
            <Typography> First Name: {telegramUser.first_name} </Typography>
            <Typography>
              {' '}
              Telegram ID: {telegramUser.telegram_user_id ?? 'N/A'}{' '}
            </Typography>
          </Box>

          <Box display="flex" gap={1}>
            <Typography> Usernames: </Typography>
            {telegramUser.telegram_usernames.length > 0 ? (
              telegramUser.telegram_usernames.map((username, index) => (
                <Chip
                  key={index}
                  label={`@${username}`}
                  size="small"
                  variant="outlined"
                />
              ))
            ) : (
              <Typography>No usernames</Typography>
            )}
          </Box>

          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Typography> Bot Permissions: </Typography>
              <Button size="small" onClick={() => handleEdit(telegramUser)}>
                Edit
              </Button>
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap">
              <PermissionChips permissions={telegramUser.permissions} />
            </Box>
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary">
              Created:{' '}
              {telegramUser.created_at.toLocaleDateString(undefined, {
                dateStyle: 'medium',
              })}{' '}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Updated:{' '}
              {telegramUser.updated_at.toLocaleDateString(undefined, {
                dateStyle: 'medium',
              }) ?? 'N/A'}{' '}
            </Typography>
            {telegramUser.deactivated_at && (
              <Typography variant="body2" color="error">
                Deactivated:{' '}
                {telegramUser.deactivated_at?.toLocaleDateString(undefined, {
                  dateStyle: 'medium',
                }) ?? 'N/A'}{' '}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}

export default UserCard
